<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue';
import { storeToRefs } from 'pinia';
import type { ArticleV1 } from '@ilteducation/content-registry-types';
import { useContentCollection } from '@ilteducation/content-collections';
import { createSearchId, useSearchContexts } from '@ilteducation/content-search';
import type { SearchQuery } from '@ilteducation/content-search';
import ArticlePreview from '@/components/ArticlePreview.vue';
import { useLocale } from '@/plugins/translation';

const { t, l } = useLocale();

const collectionId = ref(process.env['VITE_FEATURED_ARTICLES_COLLECTION'] || '');
const { collection, isLoading: isCollectionLoading } = useContentCollection(collectionId);

const articleSearchStore = useSearchContexts<ArticleV1>('Article');
const { search } = articleSearchStore;
const { result, isLoading } = storeToRefs(articleSearchStore);

const searchQuery = ref<SearchQuery | null>(null);
const latestSearchQuery = ref<SearchQuery | null>(null);

watchEffect(() => {
  if (isCollectionLoading.value) {
    return;
  }
  if (collection.value?.contents.type === 'ContentsByReferences' && collection.value.contents.references.length > 0) {
    const query: SearchQuery = {
      filter: { id: collection.value.contents.references.map((reference) => reference.id) },
      pagination: { size: 4 },
    };
    searchQuery.value = query;
    search(query, 4);
  } else {
    const query: SearchQuery = {
      filter: { isPublished: true },
      sort: [{ field: 'lastUpdatedAt', order: 'desc' }],
      pagination: { size: 4 },
    };
    latestSearchQuery.value = query;
    search(query, 4);
  }
});

const contextId = computed(() => {
  if (!searchQuery.value) {
    return '';
  }
  return createSearchId('Article', searchQuery.value);
});

const latestContextId = computed(() => {
  if (!latestSearchQuery.value) {
    return '';
  }
  return createSearchId('Article', latestSearchQuery.value);
});

const isLoadingArticles = computed(() => {
  if (!contextId.value) {
    return true;
  }
  return isLoading.value(contextId.value);
});

const isLoadingLatestArticles = computed(() => {
  if (!latestContextId.value) {
    return true;
  }
  return isLoading.value(latestContextId.value);
});

const featuredArticles = computed<ArticleV1[]>(() => {
  if (!contextId.value) {
    return [];
  }
  const articles = (result.value(contextId.value) || []) as ArticleV1[];
  if (!collection.value || collection.value.contents.type !== 'ContentsByReferences') {
    return articles;
  }
  const articleIdsInOrder = collection.value.contents.references.map((ref) => ref.id);
  return articles.toSorted((b, a) => articleIdsInOrder.indexOf(b.id) - articleIdsInOrder.indexOf(a.id));
});

const latestArticles = computed<ArticleV1[]>(() => {
  if (!latestContextId.value) {
    return [];
  }
  return (result.value(latestContextId.value) || []) as ArticleV1[];
});

const hasFeaturedArticles = computed(() => {
  return collection.value?.contents.type === 'ContentsByReferences' && collection.value.contents.references.length > 0;
});

const articlesToShow = computed<ArticleV1[]>(() => {
  return hasFeaturedArticles.value ? featuredArticles.value : latestArticles.value;
});

const isLoadingArticlesToShow = computed(() => {
  return hasFeaturedArticles.value ? isLoadingArticles.value : isLoadingLatestArticles.value;
});
</script>

<template>
  <v-sheet class="wrapper pa-4 px-sm-8 py-sm-6 mb-6">
    <v-row class="subject-list-row">
      <v-col>
        <h2 v-if="hasFeaturedArticles" class="subject-list-row--heading text-secondary ma-0 mb-3">
          {{ collection!.name }}
        </h2>
        <h2 v-else class="subject-list-row--heading text-secondary ma-0 mb-3">
          {{ t(l.videos.latest_added_articles) }}
        </h2>
        <v-row>
          <template v-if="isLoadingArticlesToShow || !articlesToShow.length">
            <v-col v-for="n in 4" :key="n" cols="6" sm="4" md="3">
              <v-skeleton-loader class="overflow-hidden" type="card" max-height="120"></v-skeleton-loader>
            </v-col>
          </template>
          <template v-else>
            <v-col v-for="article in articlesToShow" :key="article.id" cols="6" sm="4" md="3">
              <ArticlePreview :article="article" />
            </v-col>
          </template>
        </v-row>
      </v-col>
    </v-row>
  </v-sheet>
</template>

<style lang="scss" scoped>
@use '@/foundations/theme/settings';
@use 'sass:map';

.subject-list-row {
  &--heading {
    display: inline-block;
    font-size: 1.125rem;
    margin: 0 0 5px;

    @media #{map.get(settings.$display-breakpoints, 'md-and-up')} {
      font-size: 1.375rem;
      vertical-align: text-bottom;
    }
  }
}

.wrapper {
  border-left: 10px solid rgba(var(--v-theme-tertiary-fixed-dim));
  position: relative;
}
</style>
